import { z } from 'zod';
import { defineListQuery, listFilter } from './list-query.schemas';

/**
 * Accounting periods and the period lock (gitaumoses4/chukua#184, PRD → Owner reporting → Month-end close).
 * `open → closing → locked` is the normal path; `closing` still takes postings, only `locked` refuses them.
 * The list is hard-coded in a CHECK on `accounting_periods`, so a new state is a migration.
 */
export const ACCOUNTING_PERIOD_STATES = ['open', 'closing', 'locked'] as const;
export type AccountingPeriodState = (typeof ACCOUNTING_PERIOD_STATES)[number];

/** Capped at 28 so every month has the day and no period start ever needs clamping. 1 is the calendar month. */
export const MAX_PERIOD_START_DAY = 28;

/** `PUT /accounting-period-rule` — the day of the month a period starts on; applies to periods created afterwards only. */
export const setAccountingPeriodRuleInputSchema = z.object({ startDay: z.number().int().min(1).max(MAX_PERIOD_START_DAY) });
export type SetAccountingPeriodRuleInput = z.infer<typeof setAccountingPeriodRuleInputSchema>;

/** `POST /accounting-periods` — with no start the period follows the latest one; the first period must say where it starts. */
export const createAccountingPeriodInputSchema = z.object({ startDate: z.iso.date().optional() });
export type CreateAccountingPeriodInput = z.infer<typeof createAccountingPeriodInputSchema>;

/** `POST /accounting-periods/:id/unlock` — the reason is kept on the lock history and the audit entry. */
export const unlockAccountingPeriodInputSchema = z.object({
  reason: z.string().trim().min(1).max(500),
  to: z.enum(['open', 'closing']),
});
export type UnlockAccountingPeriodInput = z.infer<typeof unlockAccountingPeriodInputSchema>;

export const accountingPeriodListQuerySchema = defineListQuery({
  sortFields: ['startDate', 'endDate', 'state'],
  defaultSort: { sort: 'startDate', order: 'desc' },
  filters: { state: listFilter(z.enum(ACCOUNTING_PERIOD_STATES)) },
});
export type AccountingPeriodListQuery = z.infer<typeof accountingPeriodListQuerySchema>;

export interface AccountingPeriodRuleView {
  startDay: number;
}

export interface AccountingPeriodView {
  id: string;
  startDate: string;
  endDate: string;
  state: AccountingPeriodState;
  lockedAt: string | null;
  lockedById: string | null;
  createdAt: string;
}
